import { toPng } from 'html-to-image';
import { EuroFormat, NumberFormat } from '../../../functions/Formatters';
import { scoreLevels } from '../../../context/global';

/**
 * Convert a chart reference to a base64 PNG image
 * @param {Object} chartRef - React ref to chart component
 * @returns {Promise<string>} Base64 encoded PNG image
 */
export const chartToBase64 = async (chartRef) => {
    try {
        if (!chartRef || !chartRef.current || !chartRef.current.canvas) {
            throw new Error('Invalid chart reference');
        }
        
        const canvas = chartRef.current.canvas;
        return canvas.toDataURL('image/png');
    } catch (error) {
        console.error('Error converting chart to image:', error);
        throw error;
    }
};

/**
 * Convert a DOM element to a base64 PNG image using html-to-image
 * @param {HTMLElement} element - DOM element to convert
 * @returns {Promise<string>} Base64 encoded PNG image
 */
export const elementToBase64 = async (element) => {
    try {
        if (!element) {
            throw new Error('Invalid element');
        }
        
        return await toPng(element, {
            quality: 0.95,
            pixelRatio: 2,
        });
    } catch (error) {
        console.error('Error converting element to image:', error);
        throw error;
    }
};

/**
 * Convert base64 data URL to Buffer for docx library
 * @param {string} dataUrl - Base64 data URL
 * @returns {Buffer} Buffer containing image data
 */
export const base64ToBuffer = (dataUrl) => {
    const base64 = dataUrl.split(',')[1];
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
};

/**
 * Format a score value based on KPI type
 * @param {Object} score - Score object with value
 * @param {Object} kpi - KPI details
 * @returns {string} Formatted score
 */
export const formatScore = (score, kpi) => {
    if (!score || score.value === null || score.value === undefined) {
        return "Geen score";
    }

    if (kpi.qualitative_scale || kpi.scale_type === 0) {
        // Qualitative score
        const level = scoreLevels[score.value - 1];
        return level ? level.label : "Geen score";
    } else {
        // Quantitative score
        let total = 0;
        
        if (kpi.scale_type === 1) {
            // Detailed score (segments/layers)
            if (score.value instanceof Array) {
                try {
                    total = score.value.reduce((acc, segment) => 
                        acc + segment.values.reduce((acc2, layer) => 
                            acc2 + Number(layer.value), 0), 0);
                } catch {
                    console.log("Error calculating total score for", kpi.name);
                    total = 0;
                }
            }
            if (!isNaN(Number(score.value))) {
                total = Number(score.value);
            }
        }
        
        if (kpi.scale_type === 2) {
            // Global score
            total = Number(score.value);
        }
        
        return formatValue(total, kpi.unit);
    }
};

/**
 * Format a value with unit
 * @param {number} value - Numeric value
 * @param {string} unit - Unit string
 * @returns {string} Formatted value with unit
 */
export const formatValue = (value, unit) => {
    if (value === null || value === undefined) {
        return "N/A";
    }
    
    if (unit === "€") {
        return EuroFormat(value);
    } else if (unit) {
        return `${NumberFormat(value)} ${unit}`;
    } else {
        return NumberFormat(value);
    }
};

/**
 * Format a date for display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date
 */
export const formatDate = (date) => {
    if (!date) return "Niet ingesteld";
    
    try {
        const d = new Date(date);
        return d.toLocaleDateString('nl-NL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch {
        return "Ongeldige datum";
    }
};

/**
 * Get user names from array of user objects
 * @param {Array} users - Array of user objects
 * @returns {string} Comma-separated list of names
 */
export const formatUserList = (users) => {
    if (!users || users.length === 0) return "Geen";
    return users.map(u => u.name).join(", ");
};

/**
 * Generate a filename for the export
 * @param {Object} project - Project object
 * @returns {string} Filename
 */
export const generateFilename = (project) => {
    const projectName = project.name.replace(/[^a-z0-9]/gi, '_');
    const date = new Date().toISOString().split('T')[0];
    return `${projectName}_export_${date}.docx`;
};

/**
 * Get status label from status value
 * @param {number} statusValue - Status value
 * @returns {string} Status label
 */
export const getStatusLabel = (statusValue) => {
    const statusMap = {
        0: "Niet gestart",
        1: "In voorbereiding",
        2: "In uitvoering",
        3: "Afgerond",
        4: "Gepauzeerd"
    };
    return statusMap[statusValue] || "Onbekend";
};

/**
 * Convert SVG logo to base64 PNG image
 * @param {string} svgPath - Path to SVG file
 * @param {number} width - Desired width
 * @param {number} height - Desired height
 * @returns {Promise<string>} Base64 encoded PNG image
 */
export const svgToBase64 = (svgPath, width = 200, height = 110) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => {
            reject(new Error('Failed to load SVG image'));
        };
        img.src = svgPath;
    });
};

